// Payout requests for the Superadmin Send table (Leon 2026-10-09: "Superadmin
// presses Send before any money leaves"). Reads are the table under RLS —
// Superadmin sees every row; the encrypted columns are never selected here.
// The account is only ever decrypted by the payouts function, on request.

import type { SupabaseClient } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

// 0305's columns are not in the generated types until 0307/0308 land and the
// types are regenerated.
const db = supabase as unknown as SupabaseClient;

export type PayoutStage =
  | "pending"
  | "awaiting_approval"
  | "sending"
  | "paid"
  | "failed"
  | "returned"
  | "cancelled";

export const STAGE_LABEL: Record<PayoutStage, string> = {
  pending: "Waiting for code",
  awaiting_approval: "Ready to send",
  sending: "Sending",
  paid: "Paid",
  failed: "Failed",
  returned: "Returned",
  cancelled: "Cancelled",
};

export interface AdminPayout {
  id: string;
  staffName: string;
  staffRole: string;
  amountPhp: number;
  stage: PayoutStage;
  bankName: string | null;
  last4: string | null;
  providerRef: string | null;
  failureReason: string | null;
  createdAt: string;
  sentAt: string | null;
}

function stageOf(status: string, sentAt: string | null): PayoutStage {
  if (status === "processing") return sentAt ? "sending" : "awaiting_approval";
  return status as PayoutStage;
}

async function fetchPayouts(): Promise<AdminPayout[]> {
  const { data, error } = await db
    .from("payout_requests")
    .select(
      "id, amount_php, status, sent_at, destination_bank_name, account_number_last4, provider_ref, failure_reason, created_at, staff:staff_id(full_name, role)",
    )
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => {
    const staff = (r.staff ?? {}) as { full_name?: string; role?: string };
    return {
      id: r.id as string,
      staffName: staff.full_name ?? "Unknown",
      staffRole: staff.role ?? "",
      amountPhp: Number(r.amount_php),
      stage: stageOf(r.status as string, (r.sent_at as string | null) ?? null),
      bankName: (r.destination_bank_name as string | null) ?? null,
      last4: (r.account_number_last4 as string | null) ?? null,
      providerRef: (r.provider_ref as string | null) ?? null,
      failureReason: (r.failure_reason as string | null) ?? null,
      createdAt: r.created_at as string,
      sentAt: (r.sent_at as string | null) ?? null,
    };
  });
}

export function usePayouts() {
  return useQuery({ queryKey: ["admin-payouts"], queryFn: fetchPayouts, refetchInterval: 30_000 });
}

function usePayoutAction(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => callEdgeFunction<unknown>(path, { data: { requestId } }),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin-payouts"] }),
  });
}

export const useSendPayout = () => usePayoutAction("payouts/send");
export const useRefreshPayout = () => usePayoutAction("payouts/refresh");

/**
 * Decrypted account for one request. Never cached: fetched each time the
 * dialog opens and dropped as soon as it closes.
 */
export function usePayoutAccount(requestId: string | null) {
  return useQuery({
    queryKey: ["admin-payout-account", requestId],
    queryFn: () =>
      callEdgeFunction<{ bankName: string; accountName: string; accountNumber: string }>(
        "payouts/account",
        { data: { requestId } },
      ),
    enabled: requestId !== null,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
}
